import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { format } from "date-fns";
import { t } from "i18next";
import {
  any,
  findIndex,
  forEachObjIndexed,
  keys,
  memoizeWith,
  nth,
  remove,
} from "ramda";
import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import i18n, { Language, languageToLocale } from "../../../i18n/i18n";
import { Header } from "../../Header";
import { ChartLegendProps } from "./ChartLegend";
import { ChartLegendList } from "./ChartLegendList";
import {
  ApiDateFilter,
  ChartTimeFilter,
} from "./ChartTimeFilter/ChartTimeFilter";
import { ChartTooltip } from "./ChartTooltip";

type APIStatisticSensorCurves = {
  sensorSmoothed: { [id: string]: number };
  date: number;
  animalAge: number;
};

const data: APIStatisticSensorCurves[] = [
  {
    animalAge: 6,
    date: 1776332640000,
    sensorSmoothed: { 0: 16.1, 1: 16.1, 2: 10.1, 3: 16.4 },
  },
  {
    animalAge: 6,
    date: 1776333540000,
    sensorSmoothed: { 0: 16.5, 1: 16.5, 2: 10.5, 3: 16.4 },
  },
  {
    animalAge: 6,
    date: 1776334440000,
    sensorSmoothed: { 0: 16.9, 1: 16.8, 2: 10.5, 3: 16.4 },
  },
  {
    animalAge: 6,
    date: 1776335340000,
    sensorSmoothed: { 0: 16.9, 1: 16.9, 2: 11, 3: 16.4 },
  },
  {
    animalAge: 6,
    date: 1776336240000,
    sensorSmoothed: { 0: 17.1, 1: 17.1, 2: 11.2, 3: 16.4 },
  },
  {
    animalAge: 6,
    date: 1776337140000,
    sensorSmoothed: { 0: 17.2, 1: 17.2, 2: 11.3, 3: 16.4 },
  },
];

const LINE_STROKE_WITH = 5;

const STATS_COLORS = [
  "#B71C52",
  "#f46b10",
  "#09a509",
  "#0976a5",
  "#07b8bb",
  "#4dd15a",
  "#fbbd13",
  "#F4F4F4",
];

type CurveType = {
  name: string;
  icon: IconProp;
  color: string;
  unit: string;
};

const sensors: CurveType[] = [
  {
    name: "Capteur 1",
    icon: "thermometer-three-quarters",
    color: "#B71C52",
    unit: "°C",
  },
  {
    name: "Capteur 2",
    icon: "thermometer-three-quarters",
    color: "#f46b10",
    unit: "°C",
  },
  {
    name: "Capteur 3",
    icon: "thermometer-three-quarters",
    color: "#09a509",
    unit: "°C",
  },
  {
    name: "Capteur 4",
    icon: "thermometer-three-quarters",
    color: "#0976a5",
    unit: "°C",
  },
];

type StatData = {
  date: number;
  [x: string]:
    | string
    | number
    | number[]
    | { [x: string]: number }
    | boolean
    | { [x: string]: number[][] };
};

export type ChartTooltipProps = ChartLegendProps & {
  dataKey: string;
  unit?: string;
};

const getCurveProps: (
  data: StatData,
  type: CurveType[]
) => ChartTooltipProps[] = (data, type) => {
  let curveProps: ChartTooltipProps[] = [];
  (keys(data) as string[]).forEach((curveName: string) => {
    keys(data[curveName] as { [x: string]: number }).forEach(
      (key: string | number, index: number) => {
        curveProps.push({
          dataKey: "sensorSmoothed" + key,
          text: type[index % type.length].name,
          color: nth(index % STATS_COLORS.length, STATS_COLORS) as string,
          unit: type[index % type.length].unit,
          icon: type[index % type.length].icon,
        });
      }
    );
  });
  return curveProps;
};

type CurveData = {
  date: number;
  [x: string]: number;
};

const useCurvesData = (data: APIStatisticSensorCurves[]) => {
  const [curveProps, setCurveProps] = useState<ChartTooltipProps[]>([]);
  const [computedData, setComputedData] = useState<CurveData[]>([]);

  useEffect(() => {
    data.length && setComputedData(formatDataToRecharts(data));
    const _curveProps = getCurveProps(data[0], sensors);
    setCurveProps(_curveProps);
    // eslint-disable-next-line
  }, [data, sensors.length]);

  return {
    computedData,
    curveProps,
  };
};

const formatDataToRecharts = memoizeWith(
  (data: APIStatisticSensorCurves[]) => data.length.toString(),
  (data: APIStatisticSensorCurves[]) => {
    return data.map((d: StatData) => {
      let obj: CurveData = { date: d.date };

      (keys(d) as string[]).forEach((curveName: string) => {
        forEachObjIndexed(
          (value: number, key: string | number) => {
            obj["sensorSmoothed" + key] = value;
          },
          d["sensorSmoothed"] as { [x: string]: number }
        );
      });
      return obj;
    });
  }
);

const Chart = ({
  data,
  unit,
  curveProps,
  hiddenCurves,
}: {
  data: CurveData[];
  unit: string;
  curveProps: ChartTooltipProps[];
  hiddenCurves: string[];
}) => {
  return (
    <div className="w-full h-full text-sm">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid style={{ stroke: "var(--color-12dp)" }} />

          <XAxis
            dataKey="date"
            type="number"
            domain={["dataMin", "dataMax"]}
            axisLine={{ strokeWidth: 5 }}
            tickFormatter={(value: number) => {
              return value !== -Infinity && value !== +Infinity
                ? format(value, "d MMM HH:mm", {
                    locale: languageToLocale[i18n.language as Language],
                  })
                : "";
            }}
          />
          <YAxis
            key={unit}
            yAxisId={unit}
            domain={["dataMin", "dataMax"]}
            unit={unit}
          />

          <Tooltip
            isAnimationActive={false}
            content={<ChartTooltip displayProps={curveProps} xType="date" />}
          />

          {curveProps.map((curve) => {
            return (
              <Line
                key={curve.dataKey}
                isAnimationActive={false}
                yAxisId={curve.unit}
                type="monotone"
                dataKey={curve.dataKey}
                unit={unit}
                strokeDasharray={curve.strokeDashArray}
                strokeWidth={LINE_STROKE_WITH}
                stroke={curve.color}
                hide={any((h) => h === curve.dataKey, hiddenCurves)}
                dot={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Courbes des capteurs individuelles
export const Recharts = () => {
  const [, setDateFilter] = useState<ApiDateFilter | null>(null);
  const [hiddenCurves, setHiddenCurves] = useState<string[]>([]);

  const { computedData, curveProps } = useCurvesData(data);

  const unit = sensors[0].unit;

  return (
    <div className="w-full h-full flex flex-col">
      <div className="p-4">
        <Header
          title={t("charts")}
          icon="chart-area"
          //buttonText={t("device-link-title")}
          // /onButtonClick={() => setIsModalOpen(true)}
        />
      </div>
      <div className="flex h-full p-4">
        <ChartLegendList
          curveProps={curveProps}
          radioList
          onChange={(dataKey: string) =>
            setHiddenCurves(() => {
              const index = findIndex(
                (curve) => dataKey === curve,
                hiddenCurves
              );
              return index === -1
                ? [...hiddenCurves, dataKey]
                : remove(index, 1, hiddenCurves);
            })
          }
          unSelectedCurves={hiddenCurves}
        />
        <div className="flex-1 rounded-2xl shadow-2xl backdrop-blur-sm p-2">
          <ResponsiveContainer width="100%" height="100%">
            <Chart
              data={computedData}
              unit={unit}
              curveProps={curveProps}
              hiddenCurves={hiddenCurves}
            />
          </ResponsiveContainer>
        </div>
      </div>
      {
        <ChartTimeFilter
          onChange={(dateFilter, apiDateFilter) => {
            apiDateFilter && setDateFilter(apiDateFilter);
          }}
        />
      }
      {/*
      <div className="mt-4 px-4 pb-4">
        <ChartTimeFiltre
          onApply={(value) => {
            if (value.range.from && value.range.to) {
              setDateFilter({
                start: format(value.range.from, "yyyy-MM-dd HH:mm:ss"),
                end: format(value.range.to, "yyyy-MM-dd HH:mm:ss"),
                frequency: "hourly",
              });
            }
          }}
        />
      </div>
      */}
    </div>
  );
};
